import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Sidebar from './Sidebar';
import { useUser } from '../contexts/UserContext';
import { loadArticles } from '../utils/loadArticles';
import { Article } from '../types/article';
import { supabase } from '../lib/supabase';
import Loading from './Loading';

const ARTICLES_PER_PAGE = 10;

export function Home() {
  const { profile, loading } = useUser();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Article | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [forumIds, setForumIds] = useState<Record<string, string>>({});
  const articles: Article[] = loadArticles();
  const paginated = articles.slice((page - 1) * ARTICLES_PER_PAGE, page * ARTICLES_PER_PAGE);

  // Fetch forum IDs for all articles
  useEffect(() => {
    const fetchForumIds = async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('file_name, forum_question_id')
        .not('forum_question_id', 'is', null);

      if (error) {
        console.error('Error fetching forum IDs:', error);
        return;
      }

      const ids = data.reduce((acc, curr) => ({
        ...acc,
        [curr.file_name]: curr.forum_question_id
      }), {});
      setForumIds(ids);
    };

    fetchForumIds();
  }, []);

  // Get forum ID for an article
  const getForumId = (fileName: string): string | null => {
    return forumIds[fileName] || null;
  };

  // Create forum post from article
  const handleCreateForumPost = async (article: Article) => {
    if (!profile) {
      alert('Please log in to create forum posts');
      return;
    }

    try {
      // 1. Create the forum question
      const { data: question, error: questionError } = await supabase
        .from('questions')
        .insert({
          title: article.title,
          body: article.content,
          user_id: profile.id,
          votes: 0,
        })
        .select()
        .single();

      if (questionError) throw questionError;

      // 2. Link the article to the question
      const { error: articleError } = await supabase
        .from('articles')
        .insert({
          file_name: article.fileName,
          forum_question_id: question.id,
        });

      if (articleError) throw articleError;

      // 3. Navigate to the forum post
      window.location.href = `/forum/${question.id}`;
    } catch (error) {
      console.error('Error creating forum post:', error);
      alert('Failed to create forum post. Please try again.');
    }
  };

  // Reset sidebar state when returning to the main view
  const handleBackToFeed = () => {
    setSelected(null);
    setSidebarOpen(true);
  };

  if (loading) return <Loading />;

  if (selected) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Sidebar user={profile || { name: '', email: '', role: '', avatar_url: '', is_premium: false }} collapsed={!sidebarOpen} />
        <Box sx={{ flexGrow: 1, p: 4 }}>
          <Button onClick={handleBackToFeed} sx={{ mb: 2 }}>Back to Feed</Button>
          <Typography variant="h4" fontWeight={700} mb={2}>{selected.title}</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>{selected.date}</Typography>
          <Box mb={3}>
            {/* Render markdown content here, e.g. with react-markdown */}
            <pre style={{ whiteSpace: 'pre-wrap' }}>{selected.content}</pre>
          </Box>
          {getForumId(selected.fileName) ? (
            <Button variant="outlined" href={`/forum/${getForumId(selected.fileName)}`}>View Forum Discussion</Button>
          ) : (
            <Button variant="contained" onClick={() => handleCreateForumPost(selected)}>Discuss in Forum</Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar user={profile || { name: '', email: '', role: '', avatar_url: '', is_premium: false }} />
      <Box sx={{ 
        flexGrow: 1, 
        p: 4, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh-10px',
        gap: 3
      }}>
        <Typography variant="h4" fontWeight={700}>Articles</Typography>
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          overflowY: 'auto'
        }}>
          {paginated.map((article: Article) => (
            <Card key={article.fileName} sx={{ borderRadius: 3 }}>
              <CardHeader
                title={<Typography variant="h6">{article.title}</Typography>}
                subheader={<Typography variant="body2" color="text.secondary">{article.date}</Typography>}
              />
              <CardContent>
                <Typography variant="body1" mb={2}>{article.content.slice(0, 100)}...</Typography>
                <Button onClick={() => { setSelected(article); setSidebarOpen(false); }}>Read More</Button>
              </CardContent>
            </Card>
          ))}
        </Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          pt: 2
        }}>
          <Pagination
            count={Math.ceil(articles.length / ARTICLES_PER_PAGE)}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      </Box>
    </Box>
  );
} 